import React, { createContext, useState, useContext, useEffect } from 'react';
import { prepareBackendFetch } from '../utils/backendRequest';

const AuthContext = createContext();

const ACTIVE_COMPANY_STORAGE_KEY = 'activeCompany';

/** Realm / tenant slug from Keycloak-style issuer claim (aligned with server idp.get_tenant_id). */
function getTenantFromAccessToken(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') {
    return '';
  }
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) {
      return '';
    }
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) {
      b64 += '=';
    }
    const payload = JSON.parse(atob(b64));
    const iss = payload.iss;
    if (!iss || typeof iss !== 'string') {
      return '';
    }
    const path = new URL(iss).pathname || '';
    const segments = path.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  } catch {
    return '';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
      return undefined;
    }

    const originalFetch = window.fetch.bind(window);
    const redirectToLoginOn401 = () => {
      localStorage.removeItem('token');
      localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
      setToken(null);
      setUser(null);
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    };

    window.fetch = async (input, init) => {
      const prepared = prepareBackendFetch(input, init);
      const response = await originalFetch(prepared.input, prepared.init);
      if (response && response.status === 401) {
        redirectToLoginOn401();
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Проверяем токен при загрузке приложения
  useEffect(() => {
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  async function getUser () {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    const authIdentityUrl = process.env.REACT_APP_AUTH_IDENTITY_API_URL || '';
    const userInfoUrl = `${authIdentityUrl}/v1/auth/userinfo`;
    const userResponse = await fetch(userInfoUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });
    
    if (!userResponse.ok)
      return null;

    let user = await userResponse.json();

    const tenant = getTenantFromAccessToken(token);
    if (tenant) {
      user.tenant = tenant;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const companyResponse = await fetch(`${clientCoreApiUrl}/v1/clients/company`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (companyResponse.ok) {
      let company =  await companyResponse.json();
      // company from tenant context (non-admin users are bound to it)
      user.myCompany = company;
      user.company = company;
    }

    const introspectionUrl = `${authIdentityUrl}/v1/auth/introspect`;
 
    const introspectionResponse = await fetch(introspectionUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (introspectionResponse.ok) {
      let introspection = await introspectionResponse.json();
      user.roles = introspection.realm_access.roles;
    }

    // If an administrator selected an active company in the console UI,
    // prefer that value (purely client-side "active company" selection).
    try {
      const roles = Array.isArray(user?.roles) ? user.roles : [];
      const isAdmin = roles.includes('administrator');
      if (isAdmin) {
        const raw = localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
        if (raw) {
          const activeCompany = JSON.parse(raw);
          if (activeCompany && typeof activeCompany === 'object') {
            user.activeCompany = activeCompany;
            user.company = activeCompany;
          }
        }
      }
    } catch (e) {
      // ignore storage/parse errors
    }

    return user;
  };

  // check token validity on the server (introspection)
  const verifyToken = async (token) => {
    try {
      const authIdentityUrl = process.env.REACT_APP_AUTH_IDENTITY_API_URL || '';
      const introspectionUrl = `${authIdentityUrl}/v1/auth/introspect`;
      const introspectionResponse = await fetch(introspectionUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (introspectionResponse.ok) {
        let introspection = await introspectionResponse.json();

        if (introspection.active == false) {
          logout();
          return;
        }

        const user = await getUser();
        if (user && user.company.status == "active") {
            setUser(user);
        } else {
          setUser(null);
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const authIdentityUrl = process.env.REACT_APP_AUTH_IDENTITY_API_URL || '';
      const tokenUrl = `${authIdentityUrl}/v1/auth/token`;
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, scope: 'openid' }),
      });

      if (response.ok) {
        const data = await response.json();
        const token = data?.access_token;
        
        if (!token) {
          return { success: false, error: 'Missing access token' };
        }

        localStorage.setItem('token', token);
        setToken(token);

        const user = await getUser();
        if (!user || user.company.status != "active") {
          return { success: false, error: 'Login failed' };
        }
        setUser(user);
        
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error_description || error.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    setLoading(true);
    try {
      const u = await getUser();
      if (u && u.company?.status === "active") {
        setUser(u);
      } else {
        setUser(null);
      }
      return u;
    } finally {
      setLoading(false);
    }
  };

  const setActiveCompany = (company) => {
    if (!company) {
      localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
      setUser((prev) =>
        prev
          ? {
              ...prev,
              activeCompany: undefined,
              company: prev.myCompany,
            }
          : prev
      );
      return;
    }

    try {
      localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, JSON.stringify(company));
    } catch (e) {
      // ignore quota errors
    }

    setUser((prev) =>
      prev
        ? {
            ...prev,
            activeCompany: company,
            company,
          }
        : prev
    );
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser, setActiveCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}